"use client"

import { getUserData } from '@/lib/auth'
import { debugLog, debugError } from '@/lib/debug'

/**
 * Типы параметров состояния аналитики
 */
export type AnalyticsState = {
  // Основные настройки
  period?: string
  customStart?: string
  customEnd?: string
  /** Флаг: конец кастомного периода совпадал с «сегодня» на момент сохранения — пролонгировать при загрузке */
  customEndIsToday?: boolean
  includeAdmins?: boolean
  activeTab?: 'scenarios' | 'events' | 'sessions' | 'copilot' | 'forms'
  /** ID открытого отчёта (для deep-linking) */
  report?: number
  
  // Настройки диаграммы
  chartType?: 'area' | 'bar'
  metricType?: 'scenarios' | 'applications' | 'activeInstructions' | 'activeScenarios' | 'scenariosChanges' | 'copilotRequests' | 'activeAiFiles'
  
  // Фильтры и сортировки
  selectedScenario?: string
  selectedUser?: string
  scenariosSort?: 'new' | 'old'
  scenariosStatuses?: number[]
  eventsSort?: 'new' | 'old'
  sessionsSort?: 'new' | 'old'
  copilotSort?: 'new' | 'old'
  
  // Настройки вкладки "Отчеты по чек-листам"
  formsViewMode?: 'summary' | 'detailed'
  formsSortOrder?: 'new' | 'old'
  formsSelectedScenario?: string
  formsSelectedUser?: string
  
  // Видимость колонок (JSON-encoded Record<string, boolean>)
  scenariosColumns?: string
  eventsColumns?: string
  sessionsColumns?: string
  copilotColumns?: string
}

/**
 * Генерирует ключ localStorage для конкретного пользователя
 * @param userId - ID пользователя (если не указан, берется из getUserData())
 */
function getStorageKey(userId?: number): string {
  let finalUserId: number | string
  
  if (userId !== undefined) {
    finalUserId = userId
  } else {
    const user = getUserData()
    finalUserId = user?.id || 'guest'
  }
  
  const storageKey = `analytics-state-user-${finalUserId}`
  
  debugLog('storage', `[analytics-state] Используется ключ: ${storageKey} (userId: ${finalUserId})`)
  
  return storageKey
}

/**
 * Очищает настройки текущего пользователя
 * @param userId - ID пользователя (опционально)
 */
export function clearUserSettings(userId?: number): void {
  if (typeof window === 'undefined') return
  
  try {
    const storageKey = getStorageKey(userId)
    localStorage.removeItem(storageKey)
    debugLog('storage', `[analytics-state] Настройки очищены для ключа: ${storageKey}`)
  } catch (error) {
    debugError('Error clearing analytics state from localStorage:', error)
  }
}

/**
 * Читает параметры из URL
 */
export function readFromURL(): Partial<AnalyticsState> {
  if (typeof window === 'undefined') return {}
  
  const params = new URLSearchParams(window.location.search)
  const state: Partial<AnalyticsState> = {}
  
  // Основные настройки
  if (params.has('period')) state.period = params.get('period') || undefined
  if (params.has('customStart')) state.customStart = params.get('customStart') || undefined
  if (params.has('customEnd')) state.customEnd = params.get('customEnd') || undefined
  if (params.has('includeAdmins')) state.includeAdmins = params.get('includeAdmins') === 'true'
  if (params.has('activeTab')) {
    const tab = params.get('activeTab')
    if (tab === 'scenarios' || tab === 'events' || tab === 'sessions' || tab === 'copilot' || tab === 'forms') {
      state.activeTab = tab
    }
  }
  
  // Настройки диаграммы
  if (params.has('chartType')) {
    const chartType = params.get('chartType')
    if (chartType === 'area' || chartType === 'bar') {
      state.chartType = chartType
    }
  }
  if (params.has('metricType')) {
    const metricType = params.get('metricType')
    if (metricType === 'scenarios' || metricType === 'applications' || metricType === 'activeInstructions' || 
        metricType === 'activeScenarios' || metricType === 'scenariosChanges' || metricType === 'copilotRequests' || 
        metricType === 'activeAiFiles') {
      state.metricType = metricType
    }
  }
  
  // Фильтры и сортировки
  if (params.has('selectedScenario')) state.selectedScenario = params.get('selectedScenario') || undefined
  if (params.has('selectedUser')) state.selectedUser = params.get('selectedUser') || undefined
  
  const parseSortOrder = (value: string | null): 'new' | 'old' | undefined => {
    if (value === 'new' || value === 'old') return value
    return undefined
  }
  
  if (params.has('scenariosSort')) state.scenariosSort = parseSortOrder(params.get('scenariosSort'))
  if (params.has('eventsSort')) state.eventsSort = parseSortOrder(params.get('eventsSort'))
  if (params.has('sessionsSort')) state.sessionsSort = parseSortOrder(params.get('sessionsSort'))
  if (params.has('copilotSort')) state.copilotSort = parseSortOrder(params.get('copilotSort'))
  
  // Настройки вкладки "Отчеты по чек-листам"
  if (params.has('formsViewMode')) {
    const v = params.get('formsViewMode')
    if (v === 'summary' || v === 'detailed') state.formsViewMode = v
  }
  if (params.has('formsSortOrder')) state.formsSortOrder = parseSortOrder(params.get('formsSortOrder'))
  if (params.has('formsSelectedScenario')) state.formsSelectedScenario = params.get('formsSelectedScenario') || undefined
  if (params.has('formsSelectedUser')) state.formsSelectedUser = params.get('formsSelectedUser') || undefined
  
  // Видимость колонок
  if (params.has('scenariosColumns')) state.scenariosColumns = params.get('scenariosColumns') || undefined
  if (params.has('eventsColumns')) state.eventsColumns = params.get('eventsColumns') || undefined
  if (params.has('sessionsColumns')) state.sessionsColumns = params.get('sessionsColumns') || undefined
  if (params.has('copilotColumns')) state.copilotColumns = params.get('copilotColumns') || undefined
  if (params.has('report')) {
    const r = parseInt(params.get('report') || '')
    if (!isNaN(r)) state.report = r
  }
  
  return state
}

/**
 * Обновляет URL через window.history.pushState (без перезагрузки)
 */
export function updateURL(state: Partial<AnalyticsState>): void {
  if (typeof window === 'undefined') return
  
  const params = new URLSearchParams()
  
  // Основные настройки
  if (state.period) params.set('period', state.period)
  if (state.customStart) params.set('customStart', state.customStart)
  if (state.customEnd) params.set('customEnd', state.customEnd)
  if (state.includeAdmins !== undefined) params.set('includeAdmins', String(state.includeAdmins))
  if (state.activeTab) params.set('activeTab', state.activeTab)
  
  // Настройки диаграммы
  if (state.chartType) params.set('chartType', state.chartType)
  if (state.metricType) params.set('metricType', state.metricType)
  
  // Фильтры и сортировки
  if (state.selectedScenario) params.set('selectedScenario', state.selectedScenario)
  if (state.selectedUser) params.set('selectedUser', state.selectedUser)
  if (state.scenariosSort) params.set('scenariosSort', state.scenariosSort)
  if (state.eventsSort) params.set('eventsSort', state.eventsSort)
  if (state.sessionsSort) params.set('sessionsSort', state.sessionsSort)
  if (state.copilotSort) params.set('copilotSort', state.copilotSort)
  
  // Настройки вкладки "Отчеты по чек-листам"
  if (state.formsViewMode) params.set('formsViewMode', state.formsViewMode)
  if (state.formsSortOrder) params.set('formsSortOrder', state.formsSortOrder)
  if (state.formsSelectedScenario) params.set('formsSelectedScenario', state.formsSelectedScenario)
  if (state.formsSelectedUser) params.set('formsSelectedUser', state.formsSelectedUser)
  
  // Видимость колонок
  if (state.scenariosColumns) params.set('scenariosColumns', state.scenariosColumns)
  if (state.eventsColumns) params.set('eventsColumns', state.eventsColumns)
  if (state.sessionsColumns) params.set('sessionsColumns', state.sessionsColumns)
  if (state.copilotColumns) params.set('copilotColumns', state.copilotColumns)
  if (state.report != null) params.set('report', String(state.report))
  
  const queryString = params.toString()
  const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname
  
  window.history.pushState({}, '', newUrl)
}

/**
 * Читает из localStorage (с привязкой к текущему пользователю)
 * @param userId - ID пользователя (если не указан, берется из getUserData())
 */
export function readFromLocalStorage(userId?: number): Partial<AnalyticsState> {
  if (typeof window === 'undefined') return {}
  
  try {
    const storageKey = getStorageKey(userId)
    const stored = localStorage.getItem(storageKey)
    
    if (!stored) {
      debugLog('storage', `[analytics-state] Нет сохраненных настроек для ${storageKey}`)
      return {}
    }
    
    const parsed = JSON.parse(stored)
    debugLog('storage', `[analytics-state] Загружены настройки:`, parsed)
    return parsed as Partial<AnalyticsState>
  } catch (error) {
    debugError('Error reading from localStorage:', error)
    return {}
  }
}

/**
 * Сохраняет в localStorage (с привязкой к текущему пользователю)
 * @param state - Состояние для сохранения
 * @param userId - ID пользователя (если не указан, берется из getUserData())
 */
export function saveToLocalStorage(state: Partial<AnalyticsState>, userId?: number): void {
  if (typeof window === 'undefined') return
  
  try {
    const storageKey = getStorageKey(userId)
    localStorage.setItem(storageKey, JSON.stringify(state))
    debugLog('storage', `[analytics-state] Сохранены настройки:`, state)
  } catch (error) {
    debugError('Error saving to localStorage:', error)
  }
}

/**
 * Инициализирует состояние аналитики с учетом приоритетов:
 * 1. URL query параметры (если присутствуют)
 * 2. localStorage (если URL пустой)
 * 3. Дефолтные значения
 * @param userId - ID пользователя (если не указан, берется из getUserData())
 */
export function initializeAnalyticsState(userId?: number): Partial<AnalyticsState> {
  const urlState = readFromURL()
  const storageState = readFromLocalStorage(userId)
  
  // Объединяем с приоритетом URL > localStorage
  return {
    ...storageState,
    ...urlState,
  }
}

